// ═══════════════════════════════════════════════════════════════════════════════════════════
// ItemDrawer — the BODY of the edit level (chrome: `ClalDrawerStack`).
//
// CONTROLLED. The screen owns the draft, because the preview level diffs it and the save path
// serialises it; a second copy living in here is a second thing to keep in sync.
//
// THE THINGS IN THIS FILE THAT ARE CONTRACT, NOT TASTE:
//
//  · THE SAVE BUTTON LABEL BRANCHES ON STATUS (C6 v12 §6). On a `Status===1` item it says
//    "שמירה", NOT "שמירה כטיוטה" — saving a live item does not demote it to a draft, and the
//    old label said it did. If the hide switch is on, the publish button becomes
//    "שמירה (הפריט יישאר מוסתר)" and no publish happens at all.
//  · URL VALIDATION IS `https://` ONLY (§C2, owner-decided default). Checked on blur so typing
//    is not fought with, and again before save.
//  · A FILE IS NEVER CLEARED BEFORE A 2xx. On failure the copy is "העלאת הקובץ נכשלה — הקובץ
//    הקודם נשאר", and that has to be TRUE: `FileRef` is only written after the upload resolves.
//    On the server side the matching guarantee is SP5's COALESCE — editing a title without
//    re-uploading must not wipe the file or write a false ChangeLog row.
//  · EVERY upload call is wrapped in try/catch that also catches `TypeError`, because
//    `UploaderAPI.ts:48-55` dereferences `error.response.status` unguarded (platform bug #9). The
//    promised failure toast would otherwise never render.
//  · SIZE AND EXTENSION ARE PRE-CHECKED CLIENT-SIDE against the same limits the server carries in
//    appSettings, purely so the editor gets the frozen copy instantly instead of after a 25MB
//    round trip. THE SERVER REMAINS THE AUTHORITY — it re-checks and its answer wins.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useRef, useState } from 'react';
import moment from 'moment';
import {
    Box, Button, CircularProgress, LinearProgress, Switch, TextField, Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { InfoOutlined, InsertDriveFileOutlined, Link as LinkIcon } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import {
    CC, ClalErrorKey, FileRefDto, GroupDto, ItemDto, eClalItemStatus, eClalItemType
} from '../../../Models/ClalCenter/ClalCenter';
import { uploadClalFile, toErrorKey } from '../../../redux/reducers/clalCenterSlice';
import { DateFormats } from '../../../helpers/Constants';
import { norm, clean } from '../searchNormalizer';
import ClalStatusChip from './ClalStatusChip';
import GroupPathPicker from './GroupPathPicker';
import { KeywordChips, keywordsTooLong, MAX_KEYWORDS } from './KeywordChips';

/** Mirrors `ClalCenter.MaxFileMB` / `ClalCenter.AllowedExtensions`. The server re-checks. */
export const MAX_FILE_MB = 25;
export const ALLOWED_EXTENSIONS = [
    'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
    'png', 'jpg', 'jpeg', 'gif', 'mp3', 'mp4', 'wav'
];

// -----------------------------------------------------------------------------------------
// DIRECTION-NEUTRAL BY CONSTRUCTION. This sheet is declared in a component that mounts UNDER
// App.js's MuiThemeProvider, so jss-rtl WILL process it. Every declaration is a colour, a size
// or a weight - never a physical side - so flipping is a no-op, and an isRTL ternary in here
// would double-flip. Anything direction-dependent is an inline style at the call site.
//
// The white input fill is the high-value line: seven transparent outlines on flat #f5f6fa
// become seven white cards, which answers the "no grouping / weak hierarchy" complaint with no
// container at all. The outline colour fixes a real failure - MUI's default rgba(0,0,0,.23)
// composites to #C4C4C4 = 1.74:1 on white, under WCAG 1.4.11's 3:1 floor.
// -----------------------------------------------------------------------------------------
const useDrawerStyles = makeStyles({
    fields: {
        '& .MuiOutlinedInput-root': { backgroundColor: 'var(--pls-d-surface, #FFFFFF)' },
        '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-text-3, #7C8B9D)' },
        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-text-2, #5A6775)' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--pls-d-clal-blue, #3D6CED)', borderWidth: 2
        },
        '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--pls-d-danger, #B42318)' },
        '& .MuiFormLabel-root': { color: 'var(--pls-d-text-2, #44525E)' },
        '& .MuiFormLabel-root.Mui-focused': { color: 'var(--pls-d-clal-blue, #2F55C4)' },
        '& .MuiFormLabel-root.Mui-error': { color: 'var(--pls-d-danger, #B42318)' },
        '& .MuiFormHelperText-root': { color: 'var(--pls-d-text-2, #5A6775)', fontSize: 12.5 },
        '& .MuiFormHelperText-root.Mui-error': { color: 'var(--pls-d-danger, #B42318)' },
        // The hide Switch keeps color="primary" as its API, but the paint is Clal blue.
        '& .MuiSwitch-colorPrimary.Mui-checked': { color: 'var(--pls-d-clal-blue, #3D6CED)' },
        '& .MuiSwitch-colorPrimary.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--pls-d-clal-blue, #3D6CED)' }
    },
    typeCard: {
        '&:hover': { borderColor: 'var(--pls-d-clal-blue, #3D6CED)' },
        '&:focus': { outline: '2px solid var(--pls-d-clal-blue, #3D6CED)', outlineOffset: 2 }
    },
    // Doubling the class beats MUI's single-class .MuiButton-contained without !important;
    // the disabled rule is one specificity level higher again, so busy still greys out.
    /**
     * ADMIN CHROME IS PULSEEM, NOT CLAL (owner, 08.09.2026). The portal wears Clal's brand; the
     * buttons, fields and dialogs an editor operates it WITH are Pulseem's product and wear
     * Pulseem's. The single place Clal's palette belongs inside the admin is the preview card,
     * because that box claims to be showing the site — see PortalRowPreview.tsx.
     *
     * ccPrimary is GONE. It painted #3D6CED (כחול דיגיטלי) over the theme, which broke that rule
     * and also broke the flow: PublishPreviewDrawer's publish button is a plain color="primary",
     * so the primary action changed colour when the editor stepped from the edit level to the
     * publish level of the SAME drawer stack. Both are now color="primary" and both resolve to
     * theme.js:41's own #FF1744.
     *
     * ccSecondary and ccCancel SURVIVE, because neither was ever about brand:
     *   · the 1px #7C8B9D outline replaces MUI v4's default, which computes to 1.74:1 against
     *     white and fails SC 1.4.11 (3:1 for a control boundary). The border stays; its Clal-blue
     *     hover and Clal-navy label move to system neutrals.
     *   · ccCancel is a grey text button. It is #5A6775 — NOT because it is prettier, but because
     *     that is now the ONE secondary ink on this screen: the 08.09.2026 sweep put #5A6775 into
     *     all thirteen `color:` sites that had been carrying #7a8794 (3.24-3.67, failing AA on
     *     every possible background). Two edits earlier this button had been moved to MUI's own
     *     grey-700 #616161, which measures marginally better (5.73 on the drawer's #f5f6fa against
     *     5.35) and would have been defensible on its own — but it left the screen with two
     *     secondary greys 0.4 apart and no rule for choosing between them, which is exactly the
     *     defect the portal's own token block was written to kill ("--muted #5b6b7b and --faint
     *     #64717e, 0.49 apart with no rule for choosing").
     *
     *     TO BE PRECISE, because a comment that overclaims is worse than none: #5A6775 is now the
     *     one ink across the eighteen sites that HAD to change. It is not yet the only grey on the
     *     screen — #5b6b7b is still painted in twenty-five places at 5.48 on white, and #44525e in
     *     twelve (spelled two different ways, #44525e and #44525E). Those all PASS, so consolidating
     *     them is a token decision and not a defect fix, and it belongs to the owner rather than to
     *     this sweep.
     */
    ccSecondary: {
        '&&': { border: '1px solid var(--pls-d-text-3, #7C8B9D)', color: 'var(--pls-d-text, #424242)', fontWeight: 700, backgroundColor: 'var(--pls-d-surface, #FFFFFF)' },
        '&&:hover': { backgroundColor: 'var(--pls-d-surface-2, #F5F5F5)', borderColor: 'var(--pls-d-brand, #FF1744)' }
    },
    ccCancel: { '&&': { color: 'var(--pls-d-text-2, #5A6775)', fontWeight: 600 } }
});

const TYPE_ICON: { [k: number]: React.ComponentType<any> } = {
    [eClalItemType.LINK]: LinkIcon,
    [eClalItemType.FILE]: InsertDriveFileOutlined,
    [eClalItemType.INFO]: InfoOutlined
};

/** A draftValidationError key, mapped to the DOM id to reveal and focus. */
const PROBLEM_FIELD: { [k: string]: string } = {
    title_required: 'cc-title',
    url_required: 'cc-url',
    url_invalid: 'cc-url',
    file_required: 'cc-file',
    infotext_required: 'cc-info',
    keywords_too_long: 'cc-keywords'
};

export interface ItemDraft {
    ItemID: number | null;
    GroupID: number | null;
    ItemType: eClalItemType;
    Title: string;
    Description: string;
    Keywords: string[];
    Url: string;
    InfoText: string;
    /** The hide switch. On save this becomes a `SetStatus` call, never a `SaveItem` field. */
    Hidden: boolean;
    /** A file uploaded during THIS editing session. null ⇒ keep whatever the item already has. */
    FileRef: FileRefDto | null;
    ExistingFileName: string | null;
    ExistingFileUrl: string | null;
    /**
     * The stored file's mime type and byte size. Carried on the draft for ONE reason: the publish
     * preview prints the portal's own badge ("Excel · 6KB"), and the portal composes that from
     * exactly these two values (app.v3.js fileKind + fmtSize). Without them the preview could
     * only guess the type from the extension and could not show the size at all — so the box
     * captioned "כך ייראה הפריט באתר" would still not be showing what the site shows.
     * Display only, in both directions: SaveItem never sends them, and the server derives both
     * from the blob itself (ClalCenterLogic file-metadata derivation) rather than trusting a
     * client-supplied name.
     */
    ExistingMime: string | null;
    ExistingSizeBytes: number | null;
    /** The item's server-side status — drives the button label, never sent by SaveItem on update. */
    Status: eClalItemStatus;
    /** SPEC §8 / §2 — display only. Never sent back; SaveItem stamps these server-side. */
    UpdatedDate: string | null;
    UpdatedBy: string | null;
}

export const emptyDraft = (groupId: number | null): ItemDraft => ({
    ItemID: null,
    GroupID: groupId,
    ItemType: eClalItemType.LINK,
    Title: '',
    Description: '',
    Keywords: [],
    Url: '',
    InfoText: '',
    Hidden: false,
    FileRef: null,
    ExistingFileName: null,
    ExistingFileUrl: null,
    ExistingMime: null,
    ExistingSizeBytes: null,
    Status: eClalItemStatus.DRAFT,
    UpdatedDate: null,
    UpdatedBy: null
});

export const draftFromItem = (item: ItemDto): ItemDraft => ({
    ItemID: item.ItemID,
    GroupID: item.GroupID,
    ItemType: item.ItemType,
    Title: item.Title ?? '',
    Description: item.Description ?? '',
    Keywords: (item.Keywords ?? []).slice(),
    Url: item.ItemType === eClalItemType.LINK ? item.Url ?? '' : '',
    InfoText: item.InfoText ?? '',
    Hidden: item.Status === eClalItemStatus.HIDDEN,
    FileRef: null,
    ExistingFileName: item.FileName ?? null,
    ExistingFileUrl: item.ItemType === eClalItemType.FILE ? item.Url ?? null : null,
    ExistingMime: item.Mime ?? null,
    ExistingSizeBytes: item.SizeBytes ?? null,
    Status: item.Status,
    UpdatedDate: item.UpdatedDate ?? null,
    UpdatedBy: item.UpdatedBy ?? null
});

/**
 * The same validations the API runs, returning the SAME error keys — so the copy table is the
 * single source of wording whether the message came from here or from the server.
 */
export function draftValidationError(draft: ItemDraft): ClalErrorKey | null {
    if (!draft.Title.trim()) return 'title_required';
    if (draft.ItemType === eClalItemType.LINK) {
        if (!draft.Url.trim()) return 'url_required';
        if (!/^https:\/\//i.test(draft.Url.trim())) return 'url_invalid';
    }
    if (draft.ItemType === eClalItemType.FILE && !draft.FileRef && !draft.ExistingFileName) {
        return 'file_required';
    }
    if (draft.ItemType === eClalItemType.INFO && !draft.InfoText.trim()) return 'infotext_required';
    if (keywordsTooLong(draft.Keywords)) return 'keywords_too_long';
    return null;
}

// ── sessionStorage protection (§C6) ─────────────────────────────────────────────────────────
// A session expiry drops the user on Login.aspx through the interceptors. Without this, half an
// hour of writing an "מידע" item is gone.

const storageKey = (itemId: number | null) => `clalCenter.drawer.${itemId ?? 'new'}`;

export const saveDraftToSession = (draft: ItemDraft) => {
    try { window.sessionStorage.setItem(storageKey(draft.ItemID), JSON.stringify(draft)); } catch (e) { /* quota / private mode */ }
};
export const loadDraftFromSession = (itemId: number | null): ItemDraft | null => {
    try {
        const raw = window.sessionStorage.getItem(storageKey(itemId));
        return raw ? (JSON.parse(raw) as ItemDraft) : null;
    } catch (e) { return null; }
};
export const clearDraftSession = (itemId: number | null) => {
    try { window.sessionStorage.removeItem(storageKey(itemId)); } catch (e) { /* ignore */ }
};

interface Props {
    draft: ItemDraft;
    groups: GroupDto[];
    /** Every keyword already used in the tree — the chip suggestions. */
    keywordPool: string[];
    isRTL: boolean;
    busy: boolean;
    /** True when a session draft was restored on open, so the editor is told. */
    restoredFromSession: boolean;
    onChange: (patch: Partial<ItemDraft>) => void;
    onSave: () => void;
    onPreviewAndPublish: () => void;
    onCancel: () => void;
    onError: (errorKey: string) => void;
    onToast: (message: string) => void;
    onCreateSubGroup: (categoryId: number, title: string) => Promise<number | null>;
}

const ItemDrawer = ({
    draft, groups, keywordPool, isRTL, busy, restoredFromSession,
    onChange, onSave, onPreviewAndPublish, onCancel, onError, onToast, onCreateSubGroup
}: Props) => {
    const { t } = useTranslation();
    const sheet = useDrawerStyles();
    const [urlTouched, setUrlTouched] = useState(false);
    const [titleTouched, setTitleTouched] = useState(false);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [progress, setProgress] = useState<number | null>(null);
    const fileInput = useRef<HTMLInputElement | null>(null);

    const titleMissing = !draft.Title.trim();
    // GATED. Ungated, this painted a freshly opened, untouched, EMPTY form red - the field, its
    // helper and (through disabled) the primary button - before the editor had done anything
    // wrong. The gate is the one urlTouched already uses one field below.
    const titleError = (titleTouched || submitAttempted) && titleMissing;
    const urlBad =
        draft.ItemType === eClalItemType.LINK && urlTouched && !!draft.Url.trim() &&
        !/^https:\/\//i.test(draft.Url.trim());

    // #INDmenu-btn (Nagich) floats over the bottom-LEFT of the viewport in BOTH directions. In
    // Hebrew the paper is anchored to the physical RIGHT (ClalDrawerStack.tsx:84), so it only
    // collides when min(780px,100%) resolves to 100%. Read at render time on purpose, with no
    // resize listener: this is a controlled form that re-renders on every keystroke.
    const nagichClear = isRTL && typeof window !== 'undefined' && window.innerWidth > 820;

    const focusById = (id: string) => {
        const el = document.getElementById(id) as HTMLElement | null;
        if (!el) return;
        el.focus();
        el.scrollIntoView({ block: 'center' });
    };

    /**
     * LOAD-BEARING. ClalCenterScreen's persistItem validates, but openPreview does NOT - it only
     * branches on draft.Hidden and pushes the preview level. The old
     * disabled={busy || titleMissing || !draft.GroupID} was the only guard on that path.
     * Now that the button is live, this is.
     */
    const guard = (proceed: () => void) => {
        setSubmitAttempted(true);
        setUrlTouched(true);
        // No sub-group means nowhere to save to. The picker's inline "create a sub-group" is the
        // way out, which is exactly why it exists - so point the editor AT it.
        // NOT onError('server_error'): nothing was sent, so claiming a server fault is a lie the
        // editor cannot act on. A plain toast plus the focus jump says what is actually missing.
        if (!draft.GroupID) { onToast(t(`${CC}drawer.locationRequired`)); focusById('cc-location'); return; }
        const problem = draftValidationError(draft);
        if (problem) { onError(problem); focusById(PROBLEM_FIELD[problem] || 'cc-title'); return; }
        proceed();
    };
    const attemptPrimary = () => guard(onPreviewAndPublish);
    const attemptSave = () => guard(onSave);

    const handleFile = async (file: File | null) => {
        if (!file) return;
        const ext = (file.name.split('.').pop() || '').toLowerCase();
        if (ALLOWED_EXTENSIONS.indexOf(ext) === -1) { onError('ext_not_allowed'); return; }
        if (file.size > MAX_FILE_MB * 1024 * 1024) { onError('file_too_large'); return; }

        setProgress(0);
        try {
            const result = await uploadClalFile(file, setProgress);
            // Only NOW is the previous file replaced in the draft — never before a 2xx.
            onChange({
                FileRef: {
                    StorageName: result.StorageName,
                    RelativePath: result.RelativePath,
                    OriginalName: result.OriginalName,
                    Mime: result.Mime,
                    SizeBytes: result.SizeBytes
                },
                ExistingFileName: result.OriginalName,
                ExistingFileUrl: result.Url,
                // Kept in step with FileRef above, so the preview badge is right for a file
                // uploaded in THIS session too and not only for one already on the item.
                ExistingMime: result.Mime,
                ExistingSizeBytes: result.SizeBytes
            });
            onToast(t(`${CC}toast.fileUploaded`));
        } catch (error: any) {
            // Catches the interceptor's TypeError as well (platform bug #9).
            const key = toErrorKey(error);
            onError(key === 'server_error' ? 'server_error' : key);
            onToast(t(`${CC}toast.fileFailed`));
        } finally {
            setProgress(null);
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    const setType = (next: eClalItemType) => {
        if (next === draft.ItemType) return;
        onChange({ ItemType: next });
    };

    // "יימצא לפי" — title words + keywords, deduped through the frozen normaliser but DISPLAYED
    // in the editor's own words. Showing the normalised form would be showing them the machine.
    const findableWords = (() => {
        const raw = (draft.Title.trim() ? draft.Title.trim().split(/\s+/) : []).concat(draft.Keywords);
        const seen: { [k: string]: true } = {};
        const out: string[] = [];
        raw.forEach(w => {
            const key = norm(clean(w));
            if (!key || seen[key]) return;
            seen[key] = true;
            out.push(w);
        });
        return out;
    })();

    // A saturated brand-red fill is the wrong affordance for a SELECTED RADIO at any hue, and
    // this one also measured white-on-#FF1744 = 3.85:1, under AA's 4.5. Two published Clal hexes
    // do the job instead: tchelet fill + digital blue border. Label 8.67:1, border-on-fill
    // 3.49:1 - so the state is perceivable without relying on colour alone.
    const typeCard = (value: eClalItemType) => {
        const active = draft.ItemType === value;
        const Icon = TYPE_ICON[value];
        return (
            <button
                key={value}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => setType(value)}
                className={sheet.typeCard}
                style={{
                    // There is NO box-sizing reset in PulseemReact. This rule carries width:100%
                    // AND horizontal padding AND a border, so it declares its own border-box.
                    // Without it the card overflows its grid track leftward in RTL - which looks
                    // exactly like nothing happened at all.
                    boxSizing: 'border-box',
                    width: '100%',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: 4,
                    minHeight: 56, padding: '8px 6px',
                    font: 'inherit', fontSize: 13.5, fontWeight: active ? 700 : 500,
                    borderRadius: 8, cursor: 'pointer',
                    // 1.5px in BOTH states - animating 1 to 1.5 would shift the row by a pixel.
                    border: active ? '1.5px solid var(--pls-d-clal-blue, #3D6CED)' : '1.5px solid var(--pls-d-text-3, #7C8B9D)',
                    background: active ? 'var(--pls-d-clal-blue-tint, #D2E1FF)' : 'var(--pls-d-surface, #fff)',
                    color: active ? 'var(--pls-d-clal-blue, #293768)' : 'var(--pls-d-text-2, #44525E)'
                }}
            >
                <Icon style={{ fontSize: 20, color: active ? 'var(--pls-d-clal-blue, #293768)' : 'var(--pls-d-text-2, #5A6775)' }} />
                {t(`${CC}type.${value}`)}
            </button>
        );
    };

    const saveLabel = draft.Status === eClalItemStatus.PUBLISHED
        ? t(`${CC}drawer.save`)
        : t(`${CC}drawer.saveDraft`);

    return (
        <Box
            className={sheet.fields}
            onKeyDown={e => {
                // The twenty-item loop: Ctrl/Cmd+Enter fires the primary from anywhere in the
                // drawer. Plain Enter is left alone - the keywords Autocomplete and the INFO
                // textarea own it.
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); attemptPrimary(); }
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
        >
            {/* CONTEXT STRIP. The item's SERVER status was previously inferable inside the drawer
                only from the save button's LABEL - an editor correcting item 14 of 20 could not
                see whether the thing under their hands is live. ClalStatusChip is the only
                component allowed to render eClalItemStatus. */}
            <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', minHeight: 24 }}>
                <ClalStatusChip status={draft.Status} />
                {restoredFromSession && (
                    <Typography
                        role="status"
                        style={{ fontSize: 13, color: 'var(--pls-d-warning, #8A5A00)', background: 'var(--pls-d-warning-bg, #fff8e1)', borderRadius: 6, padding: '5px 12px' }}
                    >
                        {t(`${CC}drawer.restoredNotice`)}
                    </Typography>
                )}
            </Box>

            <GroupPathPicker
                groups={groups}
                value={draft.GroupID}
                isRTL={isRTL}
                size="small"
                inputId="cc-location"
                label={t(`${CC}drawer.fieldLocation`)}
                defaultCategoryId={null}
                onChange={groupId => onChange({ GroupID: groupId })}
                onCreateSubGroup={onCreateSubGroup}
            />

            {/* TYPE, FIRST. It decides which payload field exists two rows below, so it is the
                coarsest decision on the form, and putting it here places the two REQUIRED fields
                (name + payload) adjacent. This does not fix a reflow - the payload was already
                below the title - it fixes the reading order. */}
            <Box>
                <Typography id="cc-type-label" style={{ fontSize: 13, fontWeight: 600, color: 'var(--pls-d-text-2, #44525E)', marginBottom: 6 }}>
                    {t(`${CC}drawer.fieldType`)}
                </Typography>
                <Box
                    role="radiogroup"
                    aria-labelledby="cc-type-label"
                    style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}
                >
                    {typeCard(eClalItemType.LINK)}
                    {typeCard(eClalItemType.FILE)}
                    {typeCard(eClalItemType.INFO)}
                </Box>
            </Box>

            <TextField
                id="cc-title"
                label={t(`${CC}drawer.fieldTitle`)}
                variant="outlined"
                size="small"
                fullWidth
                required
                autoFocus={!draft.ItemID}
                value={draft.Title}
                error={titleError}
                // ' ' used to reserve a permanent blank line under this field forever. With the
                // error now gated to blur/submit, the helper renders only when it has something
                // to say; the ~19px it adds arrives together with a focus move.
                helperText={titleError ? t(`${CC}drawer.titleRequiredField`) : undefined}
                onChange={e => onChange({ Title: e.target.value })}
                onBlur={() => setTitleTouched(true)}
                // Items.Title is nvarchar(255). The cap PREVENTS over-length input; the server
                // keeps a backstop, but a T-SQL parameter assignment truncates SILENTLY (no 8152),
                // so without this the editor's text is cut with no error anywhere.
                inputProps={{ maxLength: 255 }}
            />

            {draft.ItemType === eClalItemType.LINK && (
                <TextField
                    id="cc-url"
                    label={t(`${CC}drawer.fieldUrl`)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={draft.Url}
                    error={urlBad}
                    helperText={urlBad ? t(`${CC}error.url_invalid`) : t(`${CC}drawer.urlHelp`)}
                    onChange={e => onChange({ Url: e.target.value })}
                    onBlur={() => setUrlTouched(true)}
                    // The value is a URL: it must not reorder inside an RTL paragraph
                    // (E3 RTL_NOTES 6). textAlign is branched, not 'start'.
                    // Items.Url is nvarchar(1000) — see the Title note above.
                    inputProps={{ dir: 'ltr', maxLength: 1000, style: { direction: 'ltr', textAlign: isRTL ? 'right' : 'left' } }}
                />
            )}

            {/* SPEC §3: "אפשרות לפתוח ולבדוק את היעד לפני הפרסום". A file already has this
                (openFile, below); a link had no equivalent, which is why a truncated paste could
                reach the portal unchecked. Rendered only for a URL that passed validation, so this
                never offers to open something the server would reject. */}
            {draft.ItemType === eClalItemType.LINK && !urlBad && /^https:\/\//i.test(draft.Url.trim()) && (
                <Box style={{ marginTop: -6 }}>
                    <a
                        href={draft.Url.trim()}
                        target="_blank"
                        rel="noreferrer noopener"
                        style={{ fontSize: 13, color: 'var(--pls-d-clal-blue, #2F55C4)', fontWeight: 600 }}
                    >
                        {t(`${CC}drawer.openLink`)}
                    </a>
                </Box>
            )}

            {draft.ItemType === eClalItemType.FILE && (
                <Box>
                    <Typography style={{ fontSize: 13, fontWeight: 600, color: 'var(--pls-d-text-2, #44525E)', marginBottom: 6 }}>
                        {t(`${CC}drawer.fieldFile`)}
                    </Typography>
                    <Box
                        role="button"
                        tabIndex={0}
                        onDragOver={e => e.preventDefault()}
                        onDrop={e => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0] ?? null); }}
                        onClick={() => fileInput.current?.click()}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); } }}
                        id="cc-file"
                        style={{
                            // #a9bdd4 measured 1.93:1 on #fafcff - a control boundary under WCAG
                            // 1.4.11's 3:1. #7C8B9D is 3.38:1 on the same fill.
                            border: '2px dashed var(--pls-d-text-3, #7C8B9D)', borderRadius: 12, padding: 20, textAlign: 'center',
                            background: 'var(--pls-d-surface-2, #fafcff)', cursor: 'pointer'
                        }}
                    >
                        <Typography style={{ fontSize: 14, fontWeight: 600, color: 'var(--pls-d-clal-blue, #293768)' }}>
                            {t(`${CC}drawer.dropzone`)}
                        </Typography>
                        <Typography style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5A6775)', marginTop: 4 }}>
                            {t(`${CC}drawer.dropzoneHint`)}
                        </Typography>
                        <input
                            ref={fileInput}
                            type="file"
                            accept={ALLOWED_EXTENSIONS.map(e => '.' + e).join(',')}
                            style={{ display: 'none' }}
                            onChange={e => handleFile(e.target.files?.[0] ?? null)}
                        />
                    </Box>
                    {progress !== null && (
                        <Box style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                            <LinearProgress
                                variant="determinate"
                                value={progress}
                                style={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <CircularProgress size={14} thickness={5} />
                        </Box>
                    )}
                    {!!draft.ExistingFileName && (
                        <Box
                            style={{
                                marginTop: 10, background: 'var(--pls-d-surface-2, #f8fafb)', border: '1px solid var(--pls-d-border, #e3e8ee)',
                                borderRadius: 6, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10
                            }}
                        >
                            <Typography component="span">📄</Typography>
                            <Typography
                                component="span"
                                style={{ flex: 1, fontSize: 13.5, direction: 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                            >
                                {draft.ExistingFileName}
                            </Typography>
                            {/* The scheme is asserted here for the same reason it is asserted on
                                the link field above: this value round-trips through the client, and
                                a guard that exists on one of two href sinks is not a guard. */}
                            {!!draft.ExistingFileUrl && /^https?:\/\//i.test(draft.ExistingFileUrl.trim()) && (
                                <a
                                    href={draft.ExistingFileUrl}
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    style={{ fontSize: 13, color: 'var(--pls-d-clal-blue, #2F55C4)', fontWeight: 600 }}
                                >
                                    {t(`${CC}drawer.openFile`)}
                                </a>
                            )}
                        </Box>
                    )}
                </Box>
            )}

            {/* C15: Description is wired end to end — column, DTO, published.json, and the portal
                renders it under every card — but no input existed, so it could only ever be NULL.
                Items.Description is nvarchar(500). */}
            <TextField
                id="cc-desc"
                label={t(`${CC}drawer.fieldDescription`)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={draft.Description}
                helperText={t(`${CC}drawer.descriptionHelp`)}
                onChange={e => onChange({ Description: e.target.value })}
                inputProps={{ maxLength: 500 }}
            />

            {/* SPEC §2 "הצגת מועד העדכון האחרון וסטטוס הפריט" — the status is the chip in the header;
                this is the other half. In the drawer rather than a sixth table column: the approved
                mock is five columns and a date column costs width in RTL. */}
            {!!draft.UpdatedDate && (
                <Typography style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5A6775)' }}>
                    {draft.UpdatedBy
                        ? t(`${CC}drawer.lastUpdatedBy`, {
                            when: moment(draft.UpdatedDate).format(DateFormats.DATE_TIME_24),
                            who: draft.UpdatedBy
                        })
                        : t(`${CC}drawer.lastUpdated`, {
                            when: moment(draft.UpdatedDate).format(DateFormats.DATE_TIME_24)
                        })}
                </Typography>
            )}

            {draft.ItemType === eClalItemType.INFO && (
                <TextField
                    id="cc-info"
                    label={t(`${CC}drawer.fieldInfo`)}
                    variant="outlined"
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                    value={draft.InfoText}
                    onChange={e => onChange({ InfoText: e.target.value })}
                />
            )}

            <Box>
                <Typography style={{ fontSize: 13, fontWeight: 600, color: 'var(--pls-d-text-2, #44525E)', marginBottom: 6 }}>
                    {t(`${CC}drawer.fieldKeywords`)}
                </Typography>
                <KeywordChips
                    value={draft.Keywords}
                    options={keywordPool}
                    isRTL={isRTL}
                    onChange={keywords => onChange({ Keywords: keywords })}
                    onCapReached={() => onError('keywords_too_long')}
                />
                <Box style={{ background: 'var(--pls-d-success-bg, #f2f7f4)', borderRadius: 6, padding: '8px 12px', fontSize: 12.5, color: 'var(--pls-d-success, #0B6E4F)', marginTop: 8 }}>
                    {findableWords.length ? (
                        <>
                            <Typography component="span" style={{ fontSize: 12.5, color: 'var(--pls-d-success, #0B6E4F)' }}>
                                {t(`${CC}drawer.findablePrefix`) + ' '}
                            </Typography>
                            <Typography component="span" style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--pls-d-success, #0B6E4F)' }}>
                                {findableWords.slice(0, MAX_KEYWORDS + 6).join(' · ')}
                            </Typography>
                            <Typography style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                                {t(`${CC}drawer.findableHint`)}
                            </Typography>
                        </>
                    ) : (
                        <Typography component="span" style={{ fontSize: 12.5, color: 'var(--pls-d-success, #0B6E4F)' }}>
                            {t(`${CC}drawer.findableEmpty`)}
                        </Typography>
                    )}
                </Box>
            </Box>

            <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Switch
                    color="primary"
                    checked={draft.Hidden}
                    onChange={e => onChange({ Hidden: e.target.checked })}
                    inputProps={{ 'aria-label': t(`${CC}drawer.fieldHidden`) } as any}
                />
                <Typography style={{ fontSize: 14 }}>{t(`${CC}drawer.fieldHidden`)}</Typography>
            </Box>

            <Box
                style={{
                    // position:sticky is KEPT. The appBody{overflowX:'hidden'} trap that kills
                    // sticky elsewhere in this app does not reach in here: MUI v4 portals the
                    // Drawer's Modal to document.body, OUTSIDE that div, and Drawer's paper is
                    // {overflowY:'auto', display:'flex', flexDirection:'column', height:'100%',
                    // position:'fixed'} - so the paper is this element's own scroll container.
                    // Converting to position:fixed would import the body{zoom:0.95} scaling
                    // problem for nothing.
                    position: 'sticky', bottom: 0, zIndex: 2,
                    // FULL-BLEED. ClalDrawerStack wraps children in padding:'18px 22px', so the
                    // sticky rect ended 18px above the paper edge and grey showed underneath
                    // while content scrolled through the 22px gutters. Symmetric horizontally,
                    // therefore direction-agnostic. The -22 must stay numerically equal to
                    // ClalDrawerStack's horizontal padding.
                    margin: '4px -22px -18px',
                    paddingTop: 12,
                    // sticky only ever shifts UP; without this the buttons sit flush on the edge.
                    paddingBottom: 16,
                    paddingRight: 22,
                    // #INDmenu-btn is bottom-LEFT in BOTH directions, so this inset is PHYSICAL
                    // and inline - never flipped.
                    paddingLeft: nagichClear ? 22 : 64,
                    background: 'var(--pls-d-surface, #FFFFFF)', borderTop: '1px solid var(--pls-d-border, #C9D2DE)',
                    boxShadow: '0 -2px 8px rgba(38,43,58,.06)',
                    display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center'
                }}
            >
                {/* Order and alignment UNCHANGED (start-aligned, primary first) - it matches
                    PublishPreviewDrawer, the level the editor walks into two seconds later, so
                    re-ordering here alone would make the primary jump sides mid-flow.
                    disabled is now busy ONLY: the click validates instead, which is what turns a
                    dead-looking button into one that tells you what is missing. */}
                <Button
                    variant="contained"
                    color="primary"
                    style={{ fontWeight: 700 }}
                    disabled={busy}
                    onClick={attemptPrimary}
                    startIcon={busy ? <CircularProgress size={16} thickness={5} color="inherit" /> : undefined}
                >
                    {/* Hidden means this is a SAVE, not a publish, and the label says so. */}
                    {draft.Hidden ? t(`${CC}drawer.saveHidden`) : t(`${CC}drawer.previewAndPublish`)}
                </Button>
                <Button
                    variant="outlined"
                    className={sheet.ccSecondary}
                    disabled={busy}
                    onClick={attemptSave}
                >
                    {saveLabel}
                </Button>
                <Button className={sheet.ccCancel} disabled={busy} onClick={onCancel}>
                    {t(`${CC}cancel`)}
                </Button>
            </Box>
        </Box>
    );
};

export default ItemDrawer;
